import { useRouter } from 'next/router'
import { useCallback, useMemo } from 'react'

import type { SupportedMarketKey } from '@nxt-insurance/constants'

import { DOMAIN } from '@/config/env'
import { getLocaleParts } from '@/utils/locale'

export type AlternateLink = {
  href: string
  hrefLang: string
}

export const useAlternateLinks = (
  pathnames: Record<string, { current: string } | undefined | null>,
  markets?: SupportedMarketKey[]
): AlternateLink[] => {
  const { locale, asPath, locales } = useRouter()
  const [_lang, market] = getLocaleParts(locale)
  const url = useMemo(() => new URL(asPath, `https://${DOMAIN}`), [asPath])

  const isLocaleValid = useCallback(
    (localeToValidate: string) => {
      const [_, localeMarket] = getLocaleParts(localeToValidate)
      if (markets) {
        return markets.includes(localeMarket)
      }
      return localeMarket === market
    },
    [market, markets]
  )

  const alternateLinks = useMemo(() => {
    return (
      locales
        ?.filter((l) => l !== 'default' && isLocaleValid(l) && pathnames[getLocaleParts(l)[0]])
        .map((l) => {
          const alternateUrl = new URL(url)
          const newSlug = (pathnames[getLocaleParts(l)[0]] as { current: string }).current
          alternateUrl.pathname = `/${l}${newSlug === '' ? newSlug : `/${newSlug}`}`
          alternateUrl.search = ''
          alternateUrl.hash = ''
          return {
            href: alternateUrl.toString(),
            hrefLang: l,
          }
        })
        .filter(Boolean) ?? []
    )
  }, [locales, isLocaleValid, url, pathnames])

  return alternateLinks
}
